import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { ILead } from 'src/services/api/leads/types';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { queryKeys } from 'src/services/api/constants';
import { getApiErrorMessage } from 'src/services/api/utils';

type IUpdateAssociatedLeadsResponse = {
    leads: ILead[];
}

type IUpdateAssociatedLeadsRequest = {
    coapplicant_leads_group_id: number;
    lead_uuids: string[];
}

export const useUpdateAssociatedLeads = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ILead[],
        IStandardErrorResponse,
        IUpdateAssociatedLeadsRequest
    >(async (payload) => {
        const path = `${GET_LEADS_PATH}/coapplicants`;
        const res = await apiClient(path, {
            method: 'patch',
            json: payload,
        }).json<IUpdateAssociatedLeadsResponse>();
        return res.leads;
    }, {
        onSuccess: (data, payload) => {
            const groupId = payload.coapplicant_leads_group_id;
            const leads = data;

            const onlyCurrentLead = payload.lead_uuids.length === 1;
            const allRemoved = !leads || leads.length === 0 || onlyCurrentLead ||
                (leads.length > 0 && leads.every(lead => lead.coapplicant_leads_group_id === null));

            if (allRemoved) {
                queryClient.setQueryData<ILead[]>(queryKeys.leads, (oldData) => {
                    if (!oldData) {
                        return leads || [];
                    }

                    const updated = oldData.map(lead => {
                        if (lead.coapplicant_leads_group_id === groupId) {
                            const _updatedLead = {
                                ...lead,
                                coapplicant_leads_group_id: null,
                                coapplicants: []
                            };

                            queryClient.setQueryData<ILead>([queryKeys.leads, lead.uuid], _updatedLead);
                            return _updatedLead;
                        }

                        return lead;
                    });

                    return updated;
                });

                if (leads && leads.length > 0) {
                    leads.forEach(lead => {
                        const updatedLead = {
                            ...lead,
                            coapplicant_leads_group_id: null,
                            coapplicants: []
                        };
                        queryClient.setQueryData<ILead>([queryKeys.leads, lead.uuid], updatedLead);
                    });
                }

                queryClient.invalidateQueries(queryKeys.conversations);

                addNotification('Coapplicants group updated', 'success');
                return;
            }

            const leadsWithCoapplicants = leads.map(lead => ({
                ...lead,
                coapplicants: leads.filter(l => l.uuid !== lead.uuid)
            }));

            const updatedLeadsMap = new Map(leadsWithCoapplicants.map(lead => [lead.uuid, lead]));

            queryClient.setQueryData<ILead[]>(queryKeys.leads, (oldData) => {
                if (!oldData) {
                    return leadsWithCoapplicants;
                }

                const updated = oldData.map(lead => {
                    const updatedLead = updatedLeadsMap.get(lead.uuid);
                    if (updatedLead) {
                        queryClient.setQueryData<ILead>([queryKeys.leads, lead.uuid], updatedLead);
                        return updatedLead;
                    }

                    if (groupId && lead.coapplicant_leads_group_id === groupId) {
                        const _updatedLead = {
                            ...lead,
                            coapplicant_leads_group_id: null,
                            coapplicants: []
                        };

                        queryClient.setQueryData<ILead>([queryKeys.leads, lead.uuid], _updatedLead);
                        return _updatedLead;
                    }

                    return lead;
                });

                return updated;
            });

            queryClient.invalidateQueries(queryKeys.conversations);

            addNotification('Coapplicants group updated', 'success');
        },
        onError: (error) => {
            const errorMessage = getApiErrorMessage(error) || 'Failed to update coapplicants group';
            addNotification(errorMessage, 'error');
        }
    });
};
